import { debounce } from 'lodash';
import * as Yup from 'yup';

import { api_validate_signup_email_path } from '@routes';
import request from '@utils/backend-api/request';
import { FULL_NAME_REGEX } from '@utils/constants';

interface ValidateEmailParams {
  email: string;
}

const DEBOUNCE_MS = 500;

const validateEmail = async (params: ValidateEmailParams): Promise<boolean> => {
  const response = await request('get', api_validate_signup_email_path(params));
  return response.success;
};

const debounceValidateEmail = debounce(validateEmail, DEBOUNCE_MS);

const validationSchema = (isYPPlan: boolean) =>
  Yup.object()
    .shape({
      email: Yup.string()
        .required('Email address is required')
        .email('Please enter a valid email')
        .test('validate', 'This email address already has an account', async function (email) {
          if (!this.parent.userEmail && !email) {
            // Skip validation if the initial state of email is not null
            return true;
          }

          const isValid = await debounceValidateEmail({ email });
          return isValid;
        }),
      name: Yup.string()
        .matches(FULL_NAME_REGEX, 'Please enter a full name with at least a first and last name')
        .required('Full name is required'),
      month: Yup.object().shape({
        value: Yup.number(),
        label: Yup.string(),
      }),
      day: Yup.object().shape({
        value: Yup.number(),
        label: Yup.number(),
      }),
      year: Yup.object().shape({
        value: Yup.number(),
        label: Yup.number(),
      }),
    })
    .test('dob-required', null, (schema) => {
      const { month, day, year } = schema;
      if ((month.value > 0 && day.value > 0 && year.value > 0) || !isYPPlan) return true;

      return new Yup.ValidationError('Date of birth is required.', null, 'dob');
    })
    .test('is-dob-valid', 'This plan is only available for people 30 or under.', (schema) => {
      if (!isYPPlan) return true;
      const { month, day, year } = schema;
      const birthday = new Date(year.value, month.value - 1, day.value);
      const cutoff = new Date();
      cutoff.setFullYear(cutoff.getFullYear() - 31);
      if (birthday > cutoff) return true;

      return new Yup.ValidationError(
        'This plan is only available for people 30 or under.',
        null,
        'dob'
      );
    });

export default validationSchema;
